import { Schema } from "effect"
import { Rpc, RpcGroup } from "effect/rpc"
import {
  ArtifactId,
  Epoch,
  Millis,
  NonNegInt,
  OfferId,
  PosInt,
  ProfileId,
  ProjectId,
  RepoId,
  RunId,
  RunnerId,
  Sha256,
} from "../ids"
import { SignedJob } from "../job"
import { Placement } from "../placement"
import { Lease, LeaseRef, RunError, RunKind, RunPhase, SessionRef, StopReason, Usage } from "../run"
import { ProfileCliSettings, ProfileCliWish, RunnerManifest } from "../runner"

// #region hub-rpc
export class RunnerRevoked extends Schema.TaggedError<RunnerRevoked>()("RunnerRevoked", {}) {}
export class VersionTooOld extends Schema.TaggedError<VersionTooOld>()("VersionTooOld", {
  minimum: Schema.String,
}) {}
export class ManifestRejected extends Schema.TaggedError<ManifestRejected>()("ManifestRejected", {
  why: Schema.String,
}) {}
export class StaleIncarnation extends Schema.TaggedError<StaleIncarnation>()("StaleIncarnation", {
  current: NonNegInt,
}) {}
export class OfferExpired extends Schema.TaggedError<OfferExpired>()("OfferExpired", { offerId: OfferId }) {}
export class NotEligible extends Schema.TaggedError<NotEligible>()("NotEligible", { why: Schema.String }) {}
/** The fencing error. The runner MUST stop the local process for that run when it sees it. */
export class StaleEpoch extends Schema.TaggedError<StaleEpoch>()("StaleEpoch", {
  runId: RunId,
  current: Epoch,
}) {}
export class LeaseExpired extends Schema.TaggedError<LeaseExpired>()("LeaseExpired", { runId: RunId }) {}

/** RunnerManifest.versions.protocol of runners on `RunnerHubRpcs` (runner ≥ 0.2.0). Below it: the legacy JSON socket. */
export const RUNNER_PROTOCOL_VERSION = 1

/** One profile wish of the owner-set console config (decision 0007): null = leave as configured on the runner. */
export const ConsoleProfileWish = Schema.Struct({
  id: ProfileId,
  enabled: Schema.NullOr(Schema.Boolean),
  capacity: Schema.NullOr(NonNegInt), // 0..16, checked by the hub route
  /** The profile's CLI permission/sandbox settings (rev 2.1). Absent = leave all as configured; see ProfileCliWish. */
  cli: Schema.optionalKey(ProfileCliWish),
})

/** What the runner applied for one profile, echoed in ConfigApplied. `cli` absent = a runner that predates it. */
export const AppliedProfileConfig = Schema.Struct({
  id: ProfileId,
  enabled: Schema.Boolean,
  capacity: NonNegInt,
  /** The configured CLI settings after the wish: `accessMode`, `mcp` and the overrides (null = from the access mode;
   * `effectiveCli` gives what runs pass). */
  cli: Schema.optionalKey(ProfileCliSettings),
})

export const OfferSummary = Schema.Struct({
  offerId: OfferId,
  runId: RunId,
  kind: RunKind,
  projectId: Schema.NullOr(ProjectId),
  profileId: ProfileId, // the hub picks the profile; the runner may decline
  placement: Placement, // so the runner can re-check its ceiling before claiming
  expiresAt: Millis, // 10 s
})

/** Server → runner pushes, down the one long-lived streaming RPC the runner opened (it always initiates). */
export const HubPush = Schema.TaggedUnion({
  Offer: { offer: OfferSummary },
  OfferWithdrawn: { offerId: OfferId },
  /** Lease revoked (task cancelled, owner stop, reassignment, runner paused). Runner interrupts, then reports Final{interrupted} (also before Phase{running}; a Release would requeue the run). */
  Revoke: {
    lease: LeaseRef,
    reason: Schema.Literals(["cancelled", "owner_stop", "reassigned", "runner_paused"]),
  },
  Admin: { command: Schema.Literals(["pause", "resume", "drain", "revoke", "update_available"]) },
  SignerKeys: {
    keys: Schema.Array(Schema.Struct({ kid: Schema.String, publicKey: Schema.String, notAfter: Millis })),
  },
  /** Owner-set desired config (0007). `rev` strictly increases per runner; echoed in ConfigApplied. */
  Config: {
    rev: NonNegInt,
    capacity: Schema.NullOr(PosInt),
    autoUpdate: Schema.NullOr(Schema.Boolean),
    profiles: Schema.Array(ConsoleProfileWish),
  },
  /** Re-run probes now, then UpdateManifest. */
  Refresh: {},
  /** Install this version when idle; null = latest (0010). */
  Update: { version: Schema.NullOr(Schema.String) },
  /** The workspace's registered repos. The runner maps local clones by normalized remote and advertises repoIds. */
  Repos: {
    repos: Schema.Array(
      Schema.Struct({ repoId: RepoId, remote: Schema.String, defaultBranch: Schema.String }),
    ),
  },
})

/** Control-plane facts. Milestones reach the Project DO; token streams never travel here. */
export const ReportBody = Schema.TaggedUnion({
  Phase: { phase: RunPhase }, // "running" = the process started: from now on a lost lease ends the run
  Milestone: {
    kind: Schema.Literals([
      "pr_opened",
      "tests_passed",
      "tests_failed",
      "checkpoint",
      "awaiting_permission",
      "note",
    ]),
    text: Schema.String,
    ref: Schema.NullOr(Schema.String),
  },
  ArtifactAdded: { artifactId: ArtifactId },
  /** Mechanical facts only. The agent's outcome claim arrives through the MCP endpoint (report_outcome), not here. */
  Final: {
    stopReason: StopReason,
    usage: Schema.Array(Usage),
    sessionRef: Schema.NullOr(SessionRef),
    error: Schema.NullOr(RunError),
    streamLastFrameSeq: NonNegInt, // the sink must have acked this before Final is accepted
  },
})

export const ActiveLease = Schema.Struct({
  lease: LeaseRef,
  processAlive: Schema.Boolean,
  streamLastFrameSeq: NonNegInt,
})

/** Effect RPC over the runner's control socket to its Workspace DO. Auth = runner token on the upgrade. */
export class RunnerHubRpcs extends RpcGroup.make(
  /** Starts a new incarnation; returns keep/abort verdicts for leases the runner remembers across a restart. */
  Rpc.make("Register", {
    payload: { manifest: RunnerManifest, active: Schema.Array(ActiveLease) },
    success: Schema.Struct({
      runnerId: RunnerId,
      incarnation: NonNegInt,
      serverTime: Millis,
      timing: Schema.Struct({
        leaseTtlMs: PosInt,
        renewEveryMs: PosInt,
        heartbeatEveryMs: PosInt,
        offerTtlMs: PosInt,
      }),
      verdicts: Schema.Array(
        Schema.Struct({
          lease: LeaseRef,
          verdict: Schema.Literals(["keep", "abort"]),
          expiresAt: Schema.NullOr(Millis),
        }),
      ),
    }),
    error: Schema.Union([RunnerRevoked, VersionTooOld, ManifestRejected]),
  }),
  Rpc.make("UpdateManifest", {
    payload: { incarnation: NonNegInt, manifest: RunnerManifest },
    error: Schema.Union([ManifestRejected, StaleIncarnation]),
  }),
  /** Every 10 s: liveness and capacity. Never extends a lease. */
  Rpc.make("Heartbeat", {
    payload: {
      incarnation: NonNegInt,
      freeSlots: NonNegInt,
      manifestRevision: NonNegInt,
      busyProfiles: Schema.Array(Schema.Struct({ profileId: ProfileId, running: NonNegInt })),
    },
    success: Schema.Struct({ serverTime: Millis, wantManifest: Schema.Boolean }),
    error: StaleIncarnation,
  }),
  Rpc.make("Subscribe", { payload: { incarnation: NonNegInt }, success: HubPush, stream: true }),
  /** The commit point: epoch + 1, lease (60 s), exclusive keys, MCP and stream tokens, signed job — one transaction. */
  Rpc.make("Claim", {
    payload: { incarnation: NonNegInt, offerId: OfferId, runId: RunId },
    success: Schema.Struct({ lease: Lease, job: SignedJob }),
    error: Schema.Union([OfferExpired, NotEligible, StaleIncarnation]),
  }),
  Rpc.make("Decline", {
    payload: {
      offerId: OfferId,
      reason: Schema.Literals(["busy", "ceiling", "profile_not_ready", "exclusive_local", "shutting_down"]),
      detail: Schema.NullOr(Schema.String),
    },
  }),
  /** Every 20 s (TTL/3), batched. Lost = expired | stale_epoch | revoked: stop that process now. */
  Rpc.make("Renew", {
    payload: { leases: Schema.Array(LeaseRef) },
    success: Schema.Struct({
      serverTime: Millis,
      results: Schema.Array(
        Schema.Struct({
          lease: LeaseRef,
          result: Schema.TaggedUnion({
            renewed: {
              expiresAt: Millis,
              mcpTokens: Schema.Array(
                Schema.Struct({
                  name: Schema.String,
                  token: Schema.Redacted(Schema.String),
                  expiresAt: Millis,
                }),
              ),
              /** The re-minted stream token (W8); absent from hubs before protocol 1. */
              streamToken: Schema.optionalKey(Schema.Redacted(Schema.String)),
            },
            lost: { reason: Schema.Literals(["expired", "stale_epoch", "revoked"]) },
          }),
        }),
      ),
    }),
  }),
  /** Ends a lease WITHOUT an attempt (ceiling refusal after claim, prepare failed, shutdown before start). Only
   * valid while phase = preparing; the run is offered again with the next epoch, up to MAX_PRESTART_RELEASES. */
  Rpc.make("Release", {
    payload: {
      lease: LeaseRef,
      reason: Schema.Literals(["ceiling", "prepare_failed", "shutdown"]),
      detail: Schema.NullOr(Schema.String),
    },
    error: StaleEpoch,
  }),
  /** Idempotent on (runId, epoch, seq). Final releases the lease once accepted. */
  Rpc.make("Report", {
    payload: { lease: LeaseRef, seq: PosInt, at: Millis, body: ReportBody },
    error: Schema.Union([StaleEpoch, LeaseExpired]),
  }),
  /** Console (0007): the runner applied (or refused) the desired config `rev`. */
  Rpc.make("ConfigApplied", {
    payload: {
      incarnation: NonNegInt,
      rev: NonNegInt,
      ok: Schema.Boolean,
      capacity: PosInt,
      autoUpdate: Schema.Boolean,
      profiles: Schema.Array(AppliedProfileConfig),
      error: Schema.NullOr(Schema.String),
    },
    error: StaleIncarnation,
  }),
  /** Console (0010): progress of a requested self-update. */
  Rpc.make("UpdateStatus", {
    payload: {
      incarnation: NonNegInt,
      state: Schema.Literals([
        "waiting_idle",
        "downloading",
        "installed",
        "up_to_date",
        "failed",
        "unsupported",
      ]),
      version: Schema.NullOr(Schema.String),
      message: Schema.NullOr(Schema.String),
    },
    error: StaleIncarnation,
  }),
  /** Pre-signed R2 upload for an artifact or a large pack fetch, authorised by the run's lease. Not served this wave. */
  Rpc.make("PresignUpload", {
    payload: { lease: LeaseRef, sha256: Sha256, bytes: NonNegInt, contentType: Schema.String },
    success: Schema.Struct({ url: Schema.String, key: Schema.String }),
    error: StaleEpoch,
  }),
) {}
// #endregion hub-rpc
